import { useEffect, useRef, useState } from "react"
import { useProgress } from "@react-three/drei"
import gsap from "gsap"
import { SplitText } from "gsap/all"

export default function LoaderScreen({ started, onStarted }) {
  const { progress, active, total, loaded } = useProgress()

  const [ready, setReady] = useState(false)
  const hasAnyLoadRef = useRef(false)
  const [shown, setShown] = useState(0)
  const shownRef = useRef(0)

  const tReadyRef = useRef(null)
  const btnRef = useRef()

  useEffect(() => {
    if (total > 0 || loaded > 0) hasAnyLoadRef.current = true
  }, [total, loaded])

  const targetRef = useRef(0)

  useEffect(() => {
    targetRef.current = Math.max(targetRef.current, Math.round(progress))
  }, [progress])

  useEffect(() => {
    const SPEED = 30
    const id = setInterval(() => {
      const target = targetRef.current

      if (shownRef.current < target) {
        shownRef.current = Math.min(target, shownRef.current + 1)
        setShown(shownRef.current)
      }
    }, SPEED)

    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!hasAnyLoadRef.current) return

    if (active) {
      setReady(false)
      if (tReadyRef.current) clearTimeout(tReadyRef.current)
      tReadyRef.current = null
      return
    }

    if (!tReadyRef.current) {
      tReadyRef.current = setTimeout(() => {
        setReady(true)
        tReadyRef.current = null
      }, 400)
    }

    return () => {
      if (tReadyRef.current) clearTimeout(tReadyRef.current)
      tReadyRef.current = null
    }
  }, [active])


  const requestGyroPermissionIfNeeded = async () => {
    const needsPermission =
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function";

    if (!needsPermission) return true; 

    try {
      const res = await DeviceOrientationEvent.requestPermission();
      return res === "granted";
    } catch {
      return false;
    }
  };

  const handleStart = async () => {
    if (!canStart) return
    const okGyro = await requestGyroPermissionIfNeeded();

    const p = window.__audioToggle?.()
    if (p?.then) await p
    onStarted?.()
  }

  const display = Math.round(shown)
  const canStart = display >= 100

  const waitSplit = useRef(null)
  const startSplit = useRef(null)

  useEffect(() => {
    document.fonts.ready.then(() => {
      gsap.set(".btn-start", { autoAlpha: 0 })
      gsap.set(".btn-wait", { autoAlpha: 1 })

      waitSplit.current = SplitText.create(".btn-wait", { type: "chars" })
      startSplit.current = SplitText.create(".btn-start", { type: "chars" })
      gsap.set(".btn-wait", { opacity: 1, visibility: "visible" })
      gsap.set(".btn-start", { opacity: 0, visibility: "hidden" })
      gsap.from(waitSplit.current.chars, {
        y: 20,
        opacity: 0,
        stagger: 0.05
      })
    })

    return () => {
      waitSplit.current?.revert()
      startSplit.current?.revert()
    }


  }, [])


  useEffect(() => {
    if (!waitSplit.current || !startSplit.current) return
    if (!canStart) return

    const tl = gsap.timeline()

    tl.to(waitSplit.current.chars, {
      y: -20,
      opacity: 0,
      stagger: 0.03,
      onComplete: () => gsap.set(".btn-wait", { autoAlpha: 0 })
    })

    tl.set(".btn-start", { autoAlpha: 1 })
      .from(startSplit.current.chars, {
        y: 20,
        opacity: 0,
        stagger: 0.05
      })


  }, [canStart])

  return (
    <div className={`loader-container ${started ? "hide" : ""}`}>
      <div>
        <div className="loader-img-container">
          <img src="./img/icons/loader-icon.svg" alt="" style={{ left: `calc(1vw + ${display}vw)` }} />
        </div>
      </div>
      <div className="progress-bar" style={{ width: `${display}vw` }}></div>

      <div>
        <button ref={btnRef} className={`mattone btn-loader ${canStart ? "is-ready" : ""}`} type="button" onClick={handleStart} disabled={!canStart}>
          <span className="btn-word btn-wait">WAIT</span>
          <span className="btn-word btn-start">START</span>
        </button>
        <p className="progress-pourcent mattone">{display}%</p>
      </div>
    </div>
  )
}
